"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import Button from "../../../../../components/Button";
import FormInput from "../../../../../components/FormInput";
import Checkbox from "../../../../../components/Checkbox";
import { Check } from "lucide-react";

import { useConsultation } from "../../../../../context/ConsultationContext";
import { validateDateOfBirth, validateMinorVaccinationConsentForm } from "../../utils/validation";

import styles from "./css/InjectionAdministration.module.css";

const initialForm = {
  childFullName: "",
  childDob: "",
  guardianFullName: "",
  guardianIdNumber: "",
  guardianContactNumber: "",
  hasNoKnownAllergies: false,
  hasKnownAllergies: false,
  knownAllergiesDetails: "",
  hadAllergicReactionToVaccine: false,
  allergicReactionDetails: "",
  popiaConsent: false,
  clinicalFollowUpConsent: false,
  marketingConsent: false,
  guardianSignature: "",
  guardianSignatureDate: "",
  guardianSignatureFullName: "",
  nurseOrPharmacistSignature: "",
  nurseOrPharmacistSignatureDate: "",
};

export default function MinorVaccination() {
  const navigate = useNavigate();
  const location = useLocation();
  const { consultationData, setIsInConsultation, getStepDraft, setStepDraft } = useConsultation();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayStr = useMemo(() => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(today.getDate())}-${pad(today.getMonth() + 1)}-${today.getFullYear()}`;
  }, [today]);

  const patient = consultationData?.patient || {};
  const mainMember = consultationData?.patient?.mainMember || {};

  const patientName = useMemo(() => {
    const first = String(patient?.firstname || "").trim();
    const last = String(patient?.surname || "").trim();
    const full = `${first} ${last}`.trim();
    return full;
  }, [patient?.firstname, patient?.surname]);

  const mainMemberName = useMemo(() => {
    const first = String(mainMember?.firstname || "").trim();
    const last = String(mainMember?.surname || "").trim();
    const full = `${first} ${last}`.trim();
    return full;
  }, [mainMember?.firstname, mainMember?.surname]);

  const [formData, setFormData] = useState(() => ({
    ...initialForm,
    guardianIdNumber: mainMember?.idnumber || "",
    guardianContactNumber: mainMember?.contactnumber || "",
    guardianSignatureDate: todayStr,
    nurseOrPharmacistSignatureDate: todayStr,
  }));

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === "childDob") {
      const nextValue = String(value || "").trim();
      if (!nextValue) {
        setValidationErrors((prev) => {
          if (!prev.childDob) return prev;
          const next = { ...prev };
          delete next.childDob;
          return next;
        });
        return;
      }

      const dobValidation = validateDateOfBirth(nextValue);
      setValidationErrors((prev) => {
        if (!dobValidation.isValid) {
          return { ...prev, childDob: dobValidation.error };
        }
        if (!prev.childDob) return prev;
        const next = { ...prev };
        delete next.childDob;
        return next;
      });
    }
  };

  const focusFirstError = (errors) => {
    const fieldOrder = [
      "childFullName",
      "childDob",
      "popiaConsent",
      "guardianSignature",
      "guardianSignatureFullName",
      "nurseOrPharmacistSignature",
    ];

    const field = fieldOrder.find((f) => errors?.[f]);
    if (!field) return;

    const el = document.getElementById(field) || document.querySelector(`[name="${field}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (el?.focus) el.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // Validate form
    const errors = validateMinorVaccinationConsentForm(formData);
    if (Object.keys(errors).length > 0) {
      console.log("Validation errors:", errors);
      setValidationErrors(errors);
      setTimeout(() => focusFirstError(errors), 0);
      return;
    }

    setSaveError("");
    setSaving(true);
    try {
      const payload = {
        ...formData,
        appointmentId: consultationData?.appointmentId ?? null,
        patientId: consultationData?.patientId ?? null,
      };

      // Save to consultation draft context (for DB save on Invoice Finalize)
      const existing = getStepDraft?.("consentForms", {}) || {};
      setStepDraft?.("consentForms", {
        ...existing,
        minor_vaccination: {
          answers: payload,
          patientSigned: !!formData.guardianSignature,
          patientSignedAt: formData.guardianSignatureDate ?? null,
          practitionerSigned: !!formData.nurseOrPharmacistSignature,
          practitionerSignedAt: formData.nurseOrPharmacistSignatureDate ?? null,
        },
      });

      try {
        sessionStorage.setItem("clinic.consent.minorVaccination", JSON.stringify(payload));
      } catch {}

      const continueTo = location?.state?.continueTo;
      setIsInConsultation?.(true);
      navigate(typeof continueTo === "string" && continueTo.trim() ? continueTo : "/customer/clinic/patient-overview");
    } catch (err) {
      setSaveError(err?.message || "Failed to save consent form.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <form className={styles.formContainer} onSubmit={handleSubmit}>
            <header className={styles.docHeader}>
              <div className={styles.docHeaderTop}>
                <div className={styles.pharmacyName}>Durban North Pharmacy</div>
                <div className={styles.pharmacyContact}>35 Umhlanga Rocks Drive | Tel: 031 563 7577</div>
              </div>
              <div className={styles.purpleRule} />
              <h1 className={styles.docTitle}>Consent Form — Minor Vaccination</h1>
            </header>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>1. Minor Details</h3>
              <div className={styles.grid2}>
                <div className={styles.inputGroup}>
                  <label htmlFor="childFullName">Child's Full Name</label>
                  <input
                    id="childFullName"
                    name="childFullName"
                    value={formData.childFullName}
                    onChange={handleInputChange}
                    placeholder="Child's full name"
                    className={validationErrors.childFullName ? styles.inputError : ""}
                  />
                  {validationErrors.childFullName && (
                    <div className={styles.errorMessage}>{validationErrors.childFullName}</div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="childDob">Date of Birth</label>
                  <input
                    id="childDob"
                    name="childDob"
                    value={formData.childDob}
                    onChange={handleInputChange}
                    placeholder="DD-MM-YYYY"
                    className={validationErrors.childDob ? styles.inputError : ""}
                  />
                  {validationErrors.childDob && (
                    <div className={styles.errorMessage}>{validationErrors.childDob}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Parent/Legal Guardian Details - Only show if main member data is available */}
            {mainMember && mainMemberName && (
              <div className={styles.sectionCard}>
                <h3 className={styles.sectionHeader}>2. Parent / Legal Guardian Details</h3>
                <div className={styles.grid2}>
                  <div className={styles.inputGroup}>
                    <label>Full Name</label>
                    <input
                      value={mainMemberName}
                      readOnly
                      className={styles.readOnlyInput}
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label>ID Number</label>
                    <input
                      value={mainMember.idnumber || ""}
                      readOnly
                      className={styles.readOnlyInput}
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label>Contact Number</label>
                    <input
                      value={mainMember.contactnumber || ""}
                      readOnly
                      className={styles.readOnlyInput}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>3. Allergy Declaration</h3>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id="hasNoKnownAllergies"
                  label="The minor has no known allergies"
                  checked={formData.hasNoKnownAllergies}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      hasNoKnownAllergies: e.target.checked,
                      hasKnownAllergies: e.target.checked ? false : p.hasKnownAllergies,
                      knownAllergiesDetails: e.target.checked ? "" : p.knownAllergiesDetails,
                    }))
                  }
                />
              </div>

              <div className={styles.checkboxRow}>
                <Checkbox
                  id="hasKnownAllergies"
                  label="The minor has the following known allergies:"
                  checked={formData.hasKnownAllergies}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      hasKnownAllergies: e.target.checked,
                      hasNoKnownAllergies: e.target.checked ? false : p.hasNoKnownAllergies,
                      knownAllergiesDetails: e.target.checked ? p.knownAllergiesDetails : "",
                    }))
                  }
                />
              </div>
              {formData.hasKnownAllergies && (
                <div className={styles.inputGroup}>
                  <textarea
                    name="knownAllergiesDetails"
                    value={formData.knownAllergiesDetails}
                    onChange={handleInputChange}
                    placeholder="Describe known allergies"
                    className={styles.textarea}
                  />
                </div>
              )}

              <div className={styles.checkboxRow}>
                <Checkbox
                  id="hadAllergicReactionToVaccine"
                  label="Previous allergic reaction to a vaccine or injection"
                  checked={formData.hadAllergicReactionToVaccine}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      hadAllergicReactionToVaccine: e.target.checked,
                      allergicReactionDetails: e.target.checked ? p.allergicReactionDetails : "",
                    }))
                  }
                />
              </div>
              {formData.hadAllergicReactionToVaccine && (
                <div className={styles.inputGroup}>
                  <textarea
                    name="allergicReactionDetails"
                    value={formData.allergicReactionDetails}
                    onChange={handleInputChange}
                    placeholder="If yes, describe"
                    className={styles.textarea}
                  />
                </div>
              )}
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>5. Nature of Service, Risks &amp; POPIA</h3>
              <div className={styles.infoBlock}>
                <p className={styles.statement}>
                  I understand that the minor is receiving a vaccination administered by a trained Pharmacist/Nurse in accordance with approved scope
                  of practice and national guidelines.
                </p>
                <p className={styles.statement}>
                  Possible side effects may include pain, redness, or swelling at the injection site, mild fever, headache, fatigue, and rare allergic
                  reactions.
                </p>
                <p className={styles.statement}>
                  I confirm that I have disclosed relevant medical history, allergies, pregnancy status (if applicable), and current medication use.
                </p>
              </div>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id="popiaConsent"
                  label="I consent to the use of my personal information (POPIA)."
                  checked={formData.popiaConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, popiaConsent: e.target.checked }))}
                />
              </div>
              {validationErrors.popiaConsent && (
                <div className={styles.errorMessage}>{validationErrors.popiaConsent}</div>
              )}
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>6. Communication Consent (Optional)</h3>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id="clinicalFollowUpConsent"
                  label="Clinical follow-up consent"
                  checked={formData.clinicalFollowUpConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, clinicalFollowUpConsent: e.target.checked }))}
                />
              </div>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id="marketingConsent"
                  label="Marketing and health promotions (optional)"
                  checked={formData.marketingConsent}
                  onChange={(e) => setFormData((p) => ({ ...p, marketingConsent: e.target.checked }))}
                />
              </div>
            </div>

            <div className={styles.sectionCard}>
              <h3 className={styles.sectionHeader}>7. Signatures</h3>
              <div className={styles.grid2}>
                <div className={styles.inputGroup}>
                  <label htmlFor="guardianSignature">Parent/ Guardians Signature</label>
                  <input
                    id="guardianSignature"
                    name="guardianSignature"
                    value={formData.guardianSignature}
                    onChange={handleInputChange}
                    placeholder="Type name as signature"
                    className={validationErrors.guardianSignature ? styles.inputError : ""}
                  />
                  {validationErrors.guardianSignature && (
                    <div className={styles.errorMessage}>{validationErrors.guardianSignature}</div>
                  )}
                </div>
                <div className={styles.inputGroup}>
                  <label htmlFor="guardianSignatureFullName">Full Name</label>
                  <input
                    id="guardianSignatureFullName"
                    name="guardianSignatureFullName"
                    value={formData.guardianSignatureFullName}
                    onChange={handleInputChange}
                    placeholder="Full name"
                    className={validationErrors.guardianSignatureFullName ? styles.inputError : ""}
                  />
                  {validationErrors.guardianSignatureFullName && (
                    <div className={styles.errorMessage}>{validationErrors.guardianSignatureFullName}</div>
                  )}
                </div>
                <div className={styles.inputGroup}>
                  <label>Date</label>
                  <FormInput
                    label=""
                    type="text"
                    name="guardianSignatureDate"
                    value={formData.guardianSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                  {validationErrors.guardianSignatureDate && (
                    <div className={styles.errorMessage}>{validationErrors.guardianSignatureDate}</div>
                  )}
                </div>
                <div />
              </div>

              <div className={styles.grid2}>
                <div className={styles.inputGroup}>
                  <label htmlFor="nurseOrPharmacistSignature">Nurse/Pharmacist Signature</label>
                  <input
                    id="nurseOrPharmacistSignature"
                    name="nurseOrPharmacistSignature"
                    value={formData.nurseOrPharmacistSignature}
                    onChange={handleInputChange}
                    placeholder="Type name as signature"
                    className={validationErrors.nurseOrPharmacistSignature ? styles.inputError : ""}
                  />
                  {validationErrors.nurseOrPharmacistSignature && (
                    <div className={styles.errorMessage}>{validationErrors.nurseOrPharmacistSignature}</div>
                  )}
                </div>
                <div className={styles.inputGroup}>
                  <label>Date</label>
                  <FormInput
                    label=""
                    type="text"
                    name="nurseOrPharmacistSignatureDate"
                    value={formData.nurseOrPharmacistSignatureDate}
                    onChange={() => {}}
                    placeholder="DD-MM-YYYY"
                    formatAsDate
                    disabled
                  />
                  {validationErrors.nurseOrPharmacistSignatureDate && (
                    <div className={styles.errorMessage}>{validationErrors.nurseOrPharmacistSignatureDate}</div>
                  )}
                </div>
              </div>
            </div>

            {saveError && <div className={styles.inlineError}>{saveError}</div>}
            <div className={styles.completeContainer}>
              <Button variant="primary" className={styles.completeButton} disabled={saving} type="submit">
                {saving ? (
                  "Completing..."
                ) : (
                  <>
                    Complete <Check size={22} />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}